/**
 * Pest Suggestions Module
 * Handles pest management suggestions and repellent plants
 */

// Import required modules
import { getPlantDatabase } from './plantDatabaseLoader.js';
import { pestRepellentPlants } from './pestRepellentPlants.js';
import { getPlantImageUrl } from '../lib/plantImageUrls.js';

// Track the current plants in the garden for pest repellent suggestions
let gardenPlants = {};

// Function to initialize pest repellent suggestions
async function initPestRepellentSuggestions() {
    const dbConnected = await debugDatabaseConnection();
    if (!dbConnected) {
        console.error("Database connection issues detected.");
    }
    setupPestRepellentObservers();
    updateGardenPlantsInventory();
}

// Debug function to verify database connections
async function debugDatabaseConnection() {
    console.log("Checking database connections...");
    if (!pestRepellentPlants || pestRepellentPlants.length === 0) {
        console.error("pestRepellentPlants is not defined or empty. Make sure pestRepellentPlants.js is loaded.");
        return false;
    }
    console.log(`Found ${pestRepellentPlants.length} repellent plants in pestRepellentPlants.`);
    const plantDatabase = await getPlantDatabase();
    if (!plantDatabase || Object.keys(plantDatabase).length === 0) {
        console.error("plantDatabase is not defined or empty. Make sure plantDatabase.js is loaded.");
        return false;
    }
    console.log(`Found ${Object.keys(plantDatabase).length} plants in plantDatabase.`);
    let matchCount = 0;
    for (const repellent of pestRepellentPlants) {
        const matchedName = await findClosestPlantMatch(repellent.plant);
        if (matchedName) matchCount++;
        else console.warn(`No match found for repellent plant: ${repellent.plant}`);
    }
    console.log(`Found matches for ${matchCount} out of ${pestRepellentPlants.length} repellent plants.`);
    return true;
}

// Set up observers to detect changes in the garden layout
function setupPestRepellentObservers() {
    const grid = document.getElementById('grid');
    if (!grid) return;
    
    // Create mutation observer
    const observer = new MutationObserver((mutations) => {
        let gridChanged = false;
        
        mutations.forEach((mutation) => {
            if (mutation.type === 'childList' || 
                (mutation.type === 'attributes' && 
                (mutation.attributeName === 'class' || mutation.attributeName === 'style'))) {
                gridChanged = true;
            }
        });
        
        if (gridChanged) {
            // Update the plant inventory and suggestions
            updateGardenPlantsInventory();
        }
    });
    
    // Start observing the grid for changes
    observer.observe(grid, { 
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style']
    });
}

// Update the inventory of plants in the garden
async function updateGardenPlantsInventory() {
    gardenPlants = {};
    const gridItems = document.querySelectorAll('.grid-item-5x5');
    gridItems.forEach(item => {
        const plantLabel = item.querySelector('.plant-label');
        if (plantLabel) {
            const displayName = plantLabel.textContent.trim();
            const plantName =
                (plantLabel.dataset.basePlant && String(plantLabel.dataset.basePlant).trim()) ||
                displayName;
            if (!gardenPlants[plantName]) gardenPlants[plantName] = 0;
            gardenPlants[plantName]++;
        }
    });
    await updatePestRepellentSuggestions();
}

// Update pest repellent suggestions based on garden plants
async function updatePestRepellentSuggestions() {
    const pestSuggestionButtons = document.getElementById('pest-suggestion-buttons');
    if (!pestSuggestionButtons) return;
    const selectedPestButton = pestSuggestionButtons.querySelector('.pest-repellent-button.plant-selected');
    const selectedPestPlant = selectedPestButton ? selectedPestButton.textContent.replace(/\d+$/, '').trim() : null;
    pestSuggestionButtons.innerHTML = '';
    if (Object.keys(gardenPlants).length === 0) {
        pestSuggestionButtons.innerHTML = '<div class="empty-suggestions">Populate your garden to see The best Pest Repelling Plants</div>';
        const activePestsList = document.getElementById('active-pests-list');
        if (activePestsList) activePestsList.textContent = 'No pests detected yet';
        return;
    }
    const pests = await findPestsForGardenPlants();
    displayActivePests(pests);
    const repellentPlants = await findRepellentPlantsForPests(pests);
    
    // Create a button for each repellent plant
    if (Object.keys(repellentPlants).length === 0) {
        pestSuggestionButtons.innerHTML = '<div class="empty-suggestions">No specific pest repellent plants found for your garden</div>';
    } else {
        // Sort the repellent plants by the number of pests they repel (descending)
        const sortedPlants = Object.keys(repellentPlants).sort((a, b) => {
            return repellentPlants[b].length - repellentPlants[a].length;
        });
        
        // Limit to the top 8 plants
        const displayPlants = sortedPlants.slice(0, 8);
        
        displayPlants.forEach(plant => {
            const buttonContainer = document.createElement('div');
            buttonContainer.className = 'pest-repellent-button-container';
            
            const imageClass = getPlantClass(plant);
            const imageUrl = getPlantImageUrl(imageClass);
            
            const button = document.createElement('div');
            button.className = `pest-repellent-button ${imageClass}`;
            
            if (imageUrl) {
                const img = document.createElement('img');
                img.src = imageUrl;
                img.alt = '';
                img.className = 'pest-repellent-icon';
                img.loading = 'lazy';
                button.appendChild(img);
            }
            const label = document.createElement('span');
            label.className = 'pest-repellent-label';
            label.textContent = plant;
            button.appendChild(label);
            
            if (repellentPlants[plant].length > 1) {
                const pestCount = document.createElement('span');
                pestCount.className = 'pest-count';
                pestCount.textContent = repellentPlants[plant].length;
                pestCount.title = `Repels ${repellentPlants[plant].length} pests`;
                button.appendChild(pestCount);
            }
            
            button.title = `${plant} repels: ${repellentPlants[plant].join(', ')}`;
            button.addEventListener('click', () => handleRepellentPlantClick(plant, button));
            
            if (selectedPestPlant && plant === selectedPestPlant) {
                button.classList.add('plant-selected');
            }
            
            const pestInfo = document.createElement('div');
            pestInfo.className = 'pest-info';
            pestInfo.textContent = `Repels: ${repellentPlants[plant].join(', ')}`;
            
            buttonContainer.appendChild(button);
            buttonContainer.appendChild(pestInfo);
            pestSuggestionButtons.appendChild(buttonContainer);
        });
    }
}

// Find all pests that affect the plants in the garden
async function findPestsForGardenPlants() {
    const plantDatabase = await getPlantDatabase();
    const pestsList = {};
    Object.keys(gardenPlants).forEach(plantName => {
        if (plantDatabase[plantName] && plantDatabase[plantName].pests) {
            const plantPests = plantDatabase[plantName].pests.split(',').map(pest => pest.trim());
            
            plantPests.forEach(pest => {
                if (!pestsList[pest]) {
                    pestsList[pest] = [];
                }
                
                // Add plant name to this pest if not already added
                if (!pestsList[pest].includes(plantName)) {
                    pestsList[pest].push(plantName);
                }
            });
        }
    });
    
    return pestsList;
}

// Display the list of pests that affect plants in the garden
function displayActivePests(pests) {
    const activePestsList = document.getElementById('active-pests-list');
    if (!activePestsList) return;
    
    // Clear the current list
    activePestsList.innerHTML = '';
    
    // If no pests, show a message
    if (Object.keys(pests).length === 0) {
        activePestsList.textContent = 'No pests detected yet';
        return;
    }
    
    // Create a tag for each pest
    Object.keys(pests).forEach(pest => {
        const pestTag = document.createElement('span');
        pestTag.className = 'pest-tag';
        pestTag.textContent = pest;
        pestTag.title = `Affects: ${pests[pest].join(', ')}`;
        activePestsList.appendChild(pestTag);
    });
}

// Find plants that repel the pests affecting garden plants
async function findRepellentPlantsForPests(pests) {
    const repellentPlants = {};
    for (const pest of Object.keys(pests)) {
        for (const repellent of pestRepellentPlants) {
            const repelledPests = repellent.repels.filter(repelledPest =>
                pest.toLowerCase().includes(repelledPest.toLowerCase()) ||
                repelledPest.toLowerCase().includes(pest.toLowerCase())
            );
            if (repelledPests.length > 0) {
                const matchedPlantName = await findClosestPlantMatch(repellent.plant);
                if (matchedPlantName) {
                    if (!repellentPlants[matchedPlantName]) repellentPlants[matchedPlantName] = [];
                    repelledPests.forEach(repelledPest => {
                        if (!repellentPlants[matchedPlantName].includes(pest)) {
                            repellentPlants[matchedPlantName].push(pest);
                        }
                    });
                }
            }
        }
    }
    return repellentPlants;
}

// Find the closest matching plant name in the database
async function findClosestPlantMatch(plantName) {
    const plantDatabase = await getPlantDatabase();
    if (plantDatabase[plantName]) return plantName;
    const lowerName = plantName.toLowerCase();
    for (const dbPlant in plantDatabase) {
        if (dbPlant.toLowerCase() === lowerName) return dbPlant;
    }
    for (const dbPlant in plantDatabase) {
        if (dbPlant.toLowerCase().includes(lowerName) || lowerName.includes(dbPlant.toLowerCase())) {
            return dbPlant;
        }
    }
    return null;
}

// Get the CSS class name for a plant
function getPlantClass(plantName) {
    // Convert plant name to lowercase and replace spaces with hyphens
    const className = plantName.toLowerCase().replace(/\s+/g, '-');
    
    // Check if we have a direct match in the existing CSS classes
    const allPlantButtons = document.querySelectorAll('.plantButton-container .grid-item');
    for (const button of allPlantButtons) {
        if (button.textContent.trim() === plantName) {
            // Extract the class that isn't 'grid-item'
            const classes = Array.from(button.classList);
            const imageClass = classes.find(cls => cls !== 'grid-item');
            if (imageClass) {
                return imageClass;
            }
        }
    }
    
    // Fallback to just using the transformed name
    return className;
}

// Handle click on a pest repellent plant button
function handleRepellentPlantClick(plantName, clickedButton) {
    // Clear previous pest repellent button selections
    const allPestButtons = document.querySelectorAll('.pest-repellent-button');
    allPestButtons.forEach(btn => btn.classList.remove('plant-selected'));
    
    // Highlight the clicked pest repellent button
    if (clickedButton) {
        clickedButton.classList.add('plant-selected');
    }
    
    // Select this plant in the main plant buttons container by simulating a click
    const plantButtons = document.querySelectorAll('.plantButton-container .grid-item');
    
    // Find the button with matching plant name
    let matchingButton = null;
    
    plantButtons.forEach(button => {
        if (button.textContent.trim() === plantName) {
            matchingButton = button;
        }
    });
    
    // If found, simulate a click event
    if (matchingButton) {
        matchingButton.click();
        // Plant button handler clears pest-repellent selection; restore so it matches layout highlight
        if (clickedButton) clickedButton.classList.add('plant-selected');

        // Scroll the button into view in the plant buttons container
        matchingButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
        console.warn(`Button for ${plantName} not found in plant buttons container`);
    }
}

// Main initialization function
export function initializePestSuggestions() {
    // Initialize pest repellent suggestions
    initPestRepellentSuggestions();
    
    console.log('✅ Pest suggestions functionality initialized');
}

// Export additional functions that might be needed by other modules
export {
    updateGardenPlantsInventory,
    updatePestRepellentSuggestions,
    findPestsForGardenPlants,
    displayActivePests,
    handleRepellentPlantClick
}; 